import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import api from "../api/client";
import useAuth from "../hooks/useAuth";
import useIsMobile from "../hooks/useIsMobile";
import { useConfirm } from "../context/ConfirmContext";
import Navbar from "../components/layout/Navbar";
import MobileNavbar from "../components/layout/MobileNavbar";
import CreateAccountModal from "../components/modals/CreateAccountModal";
import EditAccountModal from "../components/modals/EditAccountModal";
import { formatDate, formatDateTime } from "../utils/helpers";

/* ------------------------------------------------------------------ */
/*  Inline SVG icons                                                   */
/* ------------------------------------------------------------------ */
const SearchIcon = () => (
  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

const UserIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);

const ChevronIcon = ({ open }) => (
  <svg className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

/* ------------------------------------------------------------------ */
/*  Role group styles                                                  */
/* ------------------------------------------------------------------ */
const ROLE_STYLES = {
  "Admin": { bg: "bg-purple-50", border: "border-purple-200", badge: "bg-purple-100 text-purple-700", accent: "text-purple-700" },
  "Supervisor": { bg: "bg-blue-50", border: "border-blue-200", badge: "bg-blue-100 text-blue-700", accent: "text-blue-700" },
  "QA": { bg: "bg-teal-50", border: "border-teal-200", badge: "bg-teal-100 text-teal-700", accent: "text-teal-700" },
  "Sales Agent/TL": { bg: "bg-amber-50", border: "border-amber-200", badge: "bg-amber-100 text-amber-700", accent: "text-amber-700" },
  "Sales Agent": { bg: "bg-amber-50", border: "border-amber-200", badge: "bg-amber-100 text-amber-700", accent: "text-amber-700" },
  "Call Center Agent": { bg: "bg-cyan-50", border: "border-cyan-200", badge: "bg-cyan-100 text-cyan-700", accent: "text-cyan-700" },
  "Sales Admin": { bg: "bg-rose-50", border: "border-rose-200", badge: "bg-rose-100 text-rose-700", accent: "text-rose-700" },
  "Viewer": { bg: "bg-gray-50", border: "border-gray-200", badge: "bg-gray-100 text-gray-600", accent: "text-gray-600" },
  "FST": { bg: "bg-emerald-50", border: "border-emerald-200", badge: "bg-emerald-100 text-emerald-700", accent: "text-emerald-700" },
  "FST MIG": { bg: "bg-indigo-50", border: "border-indigo-200", badge: "bg-indigo-100 text-indigo-700", accent: "text-indigo-700" },
};

const DEFAULT_STYLE = { bg: "bg-slate-50", border: "border-slate-200", badge: "bg-slate-100 text-slate-600", accent: "text-slate-600" };

/* ------------------------------------------------------------------ */
/*  Inline Multi-TL Select (for Sales Admin TL assignment)             */
/* ------------------------------------------------------------------ */
function InlineMultiTLSelect({ user, activeTLs, onSave }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(new Set(user.assigned_tl_ids || []));
  const [saving, setSaving] = useState(false);
  const ref = useRef(null);
  const btnRef = useRef(null);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  // Sync selected from user prop when it changes
  useEffect(() => {
    setSelected(new Set(user.assigned_tl_ids || []));
  }, [user.assigned_tl_ids]);

  useEffect(() => {
    if (open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, left: rect.left });
    }
  }, [open]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  useEffect(() => {
    const handler = (e) => {
      if (
        ref.current && !ref.current.contains(e.target) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = activeTLs.filter((tl) =>
    tl.name.toLowerCase().includes(search.toLowerCase())
  );

  const handleToggle = (tlId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(tlId)) next.delete(tlId);
      else next.add(tlId);
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(user.id, [...selected]);
      setOpen(false);
    } catch {
      // parent handles error
    } finally {
      setSaving(false);
    }
  };

  const currentNames = (user.assigned_tl_names || []);
  const dirty = (() => {
    const prev = new Set(user.assigned_tl_ids || []);
    if (prev.size !== selected.size) return true;
    for (const id of selected) if (!prev.has(id)) return true;
    return false;
  })();

  return (
    <div ref={ref}>
      <button
        ref={btnRef}
        type="button"
        onClick={() => { setOpen((v) => !v); setSearch(""); }}
        className="text-left text-xs max-w-[200px] cursor-pointer hover:bg-gray-100 rounded px-1.5 py-1 transition-colors"
        title={currentNames.length > 0 ? currentNames.join(", ") : "Click to assign TLs"}
      >
        {currentNames.length === 0 ? (
          <span className="text-gray-400 italic">— assign TLs —</span>
        ) : currentNames.length <= 2 ? (
          <div className="flex flex-wrap gap-1">
            {currentNames.map((name) => (
              <span key={name} className="inline-block bg-purple-100 text-purple-800 text-[11px] font-medium px-1.5 py-0.5 rounded">
                {name}
              </span>
            ))}
          </div>
        ) : (
          <span className="inline-block bg-purple-100 text-purple-800 text-[11px] font-medium px-1.5 py-0.5 rounded">
            {currentNames.length} TLs assigned
          </span>
        )}
      </button>

      {open && createPortal(
        <div
          ref={dropdownRef}
          style={{ position: "fixed", top: pos.top, left: pos.left, zIndex: 9999 }}
          className="w-64 bg-white border rounded-lg shadow-lg flex flex-col"
        >
          <div className="p-2 border-b">
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search TL..."
              className="w-full border rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="overflow-y-auto max-h-52">
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-400">No results</div>
            ) : (
              filtered.map((tl) => (
                <label
                  key={tl.id}
                  className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-blue-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selected.has(tl.id)}
                    onChange={() => handleToggle(tl.id)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className={selected.has(tl.id) ? "font-medium text-blue-700" : "text-gray-800"}>
                    {tl.name}
                  </span>
                </label>
              ))
            )}
          </div>
          <div className="p-2 border-t flex items-center justify-between">
            <span className="text-xs text-gray-500">{selected.size} selected</span>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || !dirty}
              className="text-xs bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 disabled:opacity-50 font-medium"
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  UserTable — collapsible role-group table                           */
/* ------------------------------------------------------------------ */
function UserTable({
  title, users, currentUserId, onDeactivate, onPasswordReset, onAssignRole, onEditUser,
  deactivating, roles, canChangePassword, canDeactivateUser, canAssignRoles, canCreateUsers, isSalesAgentRole,
  isSalesAdminRole, activeTLs, onSaveTLs,
  isFSTRole, onExportFST, onImportFST, onDownloadTemplate,
}) {
  const [open, setOpen] = useState(false);
  const style = ROLE_STYLES[title] || DEFAULT_STYLE;

  return (
    <div className="mb-6">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`w-full flex items-center justify-between px-4 py-3 rounded-t-lg ${style.bg} ${style.border} border transition-colors hover:brightness-95`}
      >
        <div className="flex items-center gap-2">
          <UserIcon />
          <h2 className={`text-sm font-bold uppercase tracking-wide ${style.accent}`}>{title}</h2>
          <span className={`ml-1 text-xs font-semibold px-2 py-0.5 rounded-full ${style.badge}`}>
            {users.length}
          </span>
          {isFSTRole && (
            <div className="flex items-center gap-1.5 ml-3" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                onClick={onDownloadTemplate}
                title="Download import template"
                className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded bg-white/80 hover:bg-white text-gray-600 hover:text-gray-800 border border-gray-300 shadow-sm transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                Template
              </button>
              <button
                type="button"
                onClick={onImportFST}
                title="Import accounts from Excel"
                className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded bg-white/80 hover:bg-white text-green-700 hover:text-green-800 border border-green-300 shadow-sm transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
                Import
              </button>
              <button
                type="button"
                onClick={onExportFST}
                title="Export accounts to Excel"
                className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded bg-white/80 hover:bg-white text-blue-700 hover:text-blue-800 border border-blue-300 shadow-sm transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                Export
              </button>
            </div>
          )}
        </div>
        <ChevronIcon open={open} />
      </button>

      {open && (
        <div className={`bg-white rounded-b-lg border border-t-0 ${style.border} shadow-sm overflow-hidden`}>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gray-50/80 border-b border-gray-200">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Company</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Display Name</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Full Name</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Username</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Role</th>
                  {isSalesAgentRole && (
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Assigned TL</th>
                  )}
                  {isSalesAdminRole && (
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Assigned TLs</th>
                  )}
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Created</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Updated</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {users.length === 0 ? (
                  <tr>
                    <td colSpan={(isSalesAgentRole || isSalesAdminRole) ? 10 : 9} className="px-4 py-8 text-center text-gray-400">
                      No accounts found.
                    </td>
                  </tr>
                ) : (
                  users.map((u) => (
                    <tr key={u.id} className={`transition-colors hover:bg-gray-50/50 ${!u.is_active ? "opacity-50" : ""}`}>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{u.company_name || "—"}</td>
                      <td className="px-4 py-3 text-gray-800 font-medium">{u.display_name || "—"}</td>
                      <td className="px-4 py-3 text-gray-600">{u.full_name || "—"}</td>
                      <td className="px-4 py-3 text-gray-600 font-mono text-xs">{u.email}</td>
                      <td className="px-4 py-3">
                        {canAssignRoles ? (
                          <select
                            value={u.role_id || ""}
                            onChange={(e) => onAssignRole(u, parseInt(e.target.value))}
                            className="text-xs border border-gray-300 rounded-md px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                          >
                            <option value="" disabled>Select role</option>
                            {roles.map((r) => (
                              <option key={r.id} value={r.id}>{r.name}</option>
                            ))}
                          </select>
                        ) : (
                          <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded-full ${style.badge}`}>
                            {u.role_name || u.role}
                          </span>
                        )}
                      </td>
                      {isSalesAgentRole && (
                        <td className="px-4 py-3 text-gray-600 text-xs">{u.assigned_tl_name || "—"}</td>
                      )}
                      {isSalesAdminRole && (
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <InlineMultiTLSelect
                            user={u}
                            activeTLs={activeTLs}
                            onSave={onSaveTLs}
                          />
                        </td>
                      )}
                      <td className="px-4 py-3 text-center">
                        <span
                          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${
                            u.is_active
                              ? "bg-green-50 text-green-700 ring-1 ring-green-200"
                              : "bg-red-50 text-red-600 ring-1 ring-red-200"
                          }`}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full ${u.is_active ? "bg-green-500" : "bg-red-400"}`} />
                          {u.is_active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-500 whitespace-nowrap text-xs">{formatDate(u.created_at)}</td>
                      <td className="px-4 py-3 text-gray-500 whitespace-nowrap text-xs">{formatDateTime(u.updated_at)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-1.5">
                          {canCreateUsers && (
                            <button
                              type="button"
                              onClick={() => onEditUser(u)}
                              className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-2.5 py-1.5 rounded-md font-medium transition-colors"
                              title="Edit user details"
                            >
                              Edit
                            </button>
                          )}
                          {canChangePassword && (
                            <button
                              type="button"
                              onClick={() => onPasswordReset(u)}
                              className="text-xs bg-blue-50 hover:bg-blue-100 text-blue-700 px-2.5 py-1.5 rounded-md font-medium transition-colors"
                              title="Reset password"
                            >
                              Password
                            </button>
                          )}
                          {canDeactivateUser && u.is_active && u.id !== currentUserId ? (
                            <button
                              type="button"
                              onClick={() => onDeactivate(u)}
                              disabled={deactivating === u.id}
                              className="text-xs bg-red-50 hover:bg-red-100 text-red-700 px-2.5 py-1.5 rounded-md font-medium transition-colors disabled:opacity-50"
                              title="Deactivate account"
                            >
                              {deactivating === u.id ? "…" : "Deactivate"}
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Stats card                                                         */
/* ------------------------------------------------------------------ */
function StatCard({ label, value, color }) {
  const colors = {
    blue: "bg-blue-50 text-blue-700 border-blue-200",
    green: "bg-green-50 text-green-700 border-green-200",
    red: "bg-red-50 text-red-700 border-red-200",
    gray: "bg-gray-50 text-gray-600 border-gray-200",
  };
  return (
    <div className={`rounded-lg border px-4 py-3 ${colors[color] || colors.gray}`}>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-xs font-medium mt-0.5 opacity-80">{label}</div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main page                                                          */
/* ------------------------------------------------------------------ */
export default function ManageAccountsPage() {
  const { user, canAccessManageAccounts, canCreateUsers, canChangePassword, canDeactivateUser, canAssignRoles } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { confirm, prompt } = useConfirm();

  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [deactivating, setDeactivating] = useState(null);
  const [actionError, setActionError] = useState("");
  const [search, setSearch] = useState("");
  const [activeTLs, setActiveTLs] = useState([]);

  // FST import modal state
  const [showFSTImport, setShowFSTImport] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  useEffect(() => {
    if (!canAccessManageAccounts) navigate("/dashboard", { replace: true });
  }, [canAccessManageAccounts, navigate]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [usersRes, rolesRes, tlsRes] = await Promise.all([
        api.get("/users"),
        api.get("/roles").catch(() => ({ data: [] })),
        api.get("/team-leaders/active").catch(() => ({ data: [] })),
      ]);
      setUsers(usersRes.data);
      setRoles(rolesRes.data);
      setActiveTLs(tlsRes.data);
    } catch {
      setError("Failed to load users.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleDeactivate = async (target) => {
    const confirmed = await confirm({
      title: "Deactivate Account",
      message: `Deactivate account for "${target.display_name || target.full_name}" (${target.username || target.email})? They will no longer be able to log in.`,
      confirmLabel: "Deactivate",
      variant: "danger",
    });
    if (!confirmed) return;

    setDeactivating(target.id);
    setActionError("");
    try {
      await api.patch(`/users/${target.id}/deactivate`);
      await fetchUsers();
    } catch (err) {
      setActionError(err.response?.data?.detail || "Failed to deactivate account.");
    } finally {
      setDeactivating(null);
    }
  };

  const handlePasswordReset = async (target) => {
    const newPassword = await prompt({
      title: "Reset Password",
      message: `Enter new password for "${target.display_name || target.full_name}" (${target.username || target.email}):`,
      confirmLabel: "Reset Password",
      placeholder: "New password",
      validate: (v) => {
        if (!v) return "Password is required.";
        if (v.length < 6) return "Password must be at least 6 characters long.";
        return null;
      },
    });
    if (!newPassword) return;

    setActionError("");
    try {
      await api.patch(`/users/${target.id}/password`, { password: newPassword });
      await fetchUsers();
    } catch (err) {
      setActionError(err.response?.data?.detail || "Failed to update password.");
    }
  };

  const handleAssignRole = async (target, roleId) => {
    setActionError("");
    try {
      await api.patch(`/users/${target.id}/assign-role`, { role_id: roleId });
      await fetchUsers();
    } catch (err) {
      setActionError(err.response?.data?.detail || "Failed to assign role.");
    }
  };

  const handleSaveTLs = async (userId, tlIds) => {
    setActionError("");
    try {
      await api.put(`/users/${userId}/assigned-tls`, { team_leader_ids: tlIds });
      await fetchUsers();
    } catch (err) {
      setActionError(err.response?.data?.detail || "Failed to save TL assignment.");
      throw err; // Re-throw so the inline component knows it failed
    }
  };

  // FST / FST MIG account actions
  const handleExportFST = async () => {
    try {
      const response = await api.get("/users/fst/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "fst_accounts_export.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setActionError("Failed to export FST accounts.");
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get("/users/fst/template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "fst_accounts_template.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setActionError("Failed to download template.");
    }
  };

  const handleImportFST = async () => {
    if (!importFile) return;
    setImporting(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append("file", importFile);
      const { data } = await api.post("/users/fst/import", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResult(data);
      if (data.created > 0) await fetchUsers();
    } catch (err) {
      setImportResult({ error: err.response?.data?.detail || "Failed to import accounts." });
    } finally {
      setImporting(false);
    }
  };

  // Filter users by search term
  const filteredUsers = useMemo(() => {
    if (!search.trim()) return users;
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      const displayName = (u.display_name || "").toLowerCase();
      const fullName = (u.full_name || "").toLowerCase();
      const uname = (u.username || u.email || "").toLowerCase();
      const role = (u.role_name || u.role || "").toLowerCase();
      const company = (u.company_name || "").toLowerCase();
      const tl = (u.assigned_tl_name || "").toLowerCase();
      const tlNames = (u.assigned_tl_names || []).join(" ").toLowerCase();
      return displayName.includes(q) || fullName.includes(q) || uname.includes(q) || role.includes(q) || company.includes(q) || tl.includes(q) || tlNames.includes(q);
    });
  }, [users, search]);

  // Stats
  const totalUsers = users.length;
  const activeUsers = users.filter((u) => u.is_active).length;
  const inactiveUsers = totalUsers - activeUsers;

  // Group users by their role_name
  const groupedUsers = {};
  for (const u of filteredUsers) {
    const groupKey = u.role_name || u.role || "Unknown";
    if (!groupedUsers[groupKey]) groupedUsers[groupKey] = [];
    groupedUsers[groupKey].push(u);
  }
  for (const key of Object.keys(groupedUsers)) {
    groupedUsers[key].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }
  const groupOrder = ["Admin", "Supervisor", "QA", "Sales Agent/TL", "Sales Agent", "Sales Admin", "Call Center Agent", "Viewer", "FST", "FST MIG"];
  const sortedGroups = groupOrder
    .filter((g) => groupedUsers[g] && groupedUsers[g].length > 0)
    .map((g) => [g, groupedUsers[g]]);
  for (const key of Object.keys(groupedUsers)) {
    if (!groupOrder.includes(key)) {
      sortedGroups.push([key, groupedUsers[key]]);
    }
  }

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {isMobile ? <MobileNavbar /> : <Navbar />}
      <main className="flex-1 p-4 lg:p-6 max-w-[1600px] w-full mx-auto">

        {/* Page header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Manage Accounts</h1>
            <p className="text-sm text-gray-500 mt-1">Create, edit, and manage user accounts across the system.</p>
          </div>
          {canCreateUsers && (
            <button
              onClick={() => setShowCreate(true)}
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-5 py-2.5 rounded-lg shadow-sm transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Add Account
            </button>
          )}
        </div>

        {/* Stats cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <StatCard label="Total Accounts" value={totalUsers} color="blue" />
          <StatCard label="Active" value={activeUsers} color="green" />
          <StatCard label="Inactive" value={inactiveUsers} color="red" />
          <StatCard label="Role Groups" value={sortedGroups.length} color="gray" />
        </div>

        {/* Search bar */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
              <SearchIcon />
            </div>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, username, role, company, or TL..."
              className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent shadow-sm placeholder-gray-400"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-lg leading-none"
                title="Clear search"
              >
                &times;
              </button>
            )}
          </div>
        </div>

        {/* Error messages */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3 mb-4 flex items-center gap-2">
            <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            {error}
          </div>
        )}
        {actionError && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3 mb-4 flex items-center gap-2">
            <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            {actionError}
            <button onClick={() => setActionError("")} className="ml-auto text-red-400 hover:text-red-600">&times;</button>
          </div>
        )}

        {/* Main content */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-3 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
              <span className="text-sm text-gray-500">Loading accounts…</span>
            </div>
          </div>
        ) : (
          <>
            {sortedGroups.length === 0 && search.trim() && (
              <div className="text-center py-16">
                <div className="text-gray-300 mb-3">
                  <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <p className="text-gray-500 text-sm">No accounts match "<span className="font-medium">{search}</span>"</p>
              </div>
            )}
            {sortedGroups.map(([groupName, groupUsers]) => (
              <UserTable
                key={groupName}
                title={`${groupName} Accounts`}
                users={groupUsers}
                currentUserId={user?.id}
                onDeactivate={handleDeactivate}
                onPasswordReset={handlePasswordReset}
                onAssignRole={handleAssignRole}
                onEditUser={setEditUser}
                deactivating={deactivating}
                roles={roles}
                canChangePassword={canChangePassword}
                canDeactivateUser={canDeactivateUser}
                canAssignRoles={canAssignRoles}
                canCreateUsers={canCreateUsers}
                isSalesAgentRole={groupName === "Sales Agent/TL" || groupName === "Sales Agent"}
                isSalesAdminRole={groupName === "Sales Admin"}
                activeTLs={activeTLs}
                onSaveTLs={handleSaveTLs}
                isFSTRole={(groupName === "FST" || groupName === "FST MIG") && canCreateUsers}
                onExportFST={handleExportFST}
                onImportFST={() => { setImportFile(null); setImportResult(null); setShowFSTImport(true); }}
                onDownloadTemplate={handleDownloadTemplate}
              />
            ))}
          </>
        )}
      </main>

      {showCreate && (
        <CreateAccountModal
          onClose={() => setShowCreate(false)}
          onRefresh={fetchUsers}
        />
      )}

      {editUser && (
        <EditAccountModal
          user={editUser}
          onClose={() => setEditUser(null)}
          onRefresh={fetchUsers}
        />
      )}

      {/* FST Import Modal */}
      {showFSTImport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Import FST / FST MIG Accounts</h3>
              <button onClick={() => setShowFSTImport(false)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
            </div>

            {!importResult ? (
              <>
                <p className="text-sm text-gray-600 mb-4">
                  Upload an Excel file (.xlsx) with columns: <span className="font-medium">Display Name, Full Name, Username, Password, Company, Role</span>.
                  Role must be <span className="font-medium text-emerald-700">FST</span> or <span className="font-medium text-indigo-700">FST MIG</span>.
                </p>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 mb-4 text-center hover:border-blue-400 transition-colors">
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                    className="block w-full text-sm text-gray-500 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer"
                  />
                  {importFile && (
                    <p className="text-xs text-gray-500 mt-2">Selected: <span className="font-medium text-gray-700">{importFile.name}</span></p>
                  )}
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setShowFSTImport(false)}
                    className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-gray-600"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleImportFST}
                    disabled={!importFile || importing}
                    className="px-4 py-2 text-sm text-white rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-50 transition-colors"
                  >
                    {importing ? "Importing…" : "Import"}
                  </button>
                </div>
              </>
            ) : importResult.error ? (
              <>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-red-700 font-medium">{importResult.error}</p>
                </div>
                <div className="flex justify-end">
                  <button onClick={() => setShowFSTImport(false)} className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50 text-gray-600">Close</button>
                </div>
              </>
            ) : (
              <>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                  <p className="text-sm font-medium text-green-800 mb-2">Import Complete</p>
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div>
                      <p className="text-xl font-bold text-gray-900">{importResult.total}</p>
                      <p className="text-xs text-gray-500">Total Rows</p>
                    </div>
                    <div>
                      <p className="text-xl font-bold text-green-700">{importResult.created}</p>
                      <p className="text-xs text-gray-500">Created</p>
                    </div>
                    <div>
                      <p className="text-xl font-bold text-amber-600">{importResult.skipped}</p>
                      <p className="text-xs text-gray-500">Skipped</p>
                    </div>
                  </div>
                </div>
                {importResult.errors && importResult.errors.length > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 max-h-40 overflow-y-auto">
                    <p className="text-xs font-semibold text-amber-700 mb-1">Details:</p>
                    {importResult.errors.map((err, i) => (
                      <p key={i} className="text-xs text-amber-600">{err}</p>
                    ))}
                  </div>
                )}
                <div className="flex justify-end">
                  <button onClick={() => setShowFSTImport(false)} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">Done</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
